import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { Plus, Search, Edit, Trash2, Eye, Truck } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Button } from '../components/ui/button';

const MachineryList = () => {
  const [machinery, setMachinery] = useState([]);
  const [filteredMachinery, setFilteredMachinery] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMachinery, setEditingMachinery] = useState(null);
  const [formData, setFormData] = useState({
  // =========================
  // IDENTIFICACIÓN
  // =========================
  name: '',
  internal_code: '',
  type: '',
  brand: '',
  model: '',
  serial_number: '',
  chassis_vin: '',
  year: new Date().getFullYear(),

  // =========================
  // OPERACIÓN
  // =========================
  engine_hours: 0,
  odometer: 0,
  status: 'operational',
  responsible: '',
  location: '',
  project: '',
  notes: '',

  // =========================
  // MOTOR
  // =========================
  engine_brand: '',
  engine_model: '',
  engine_serial: '',
  engine_configuration: '',
  engine_displacement: 0,
  engine_displacement_unit: 'L',
  engine_hp: 0,
  has_turbo: false,

  // =========================
  // COMBUSTIBLE
  // =========================
  fuel_type: 'diesel',
  fuel_tank_capacity: 0,
  fuel_tank_unit: 'L',
  fuel_consumption: 0,
  fuel_consumption_unit: 'L/h',

  // =========================
  // LUBRICANTES
  // =========================
  engine_oil: null,
  transmission_oil: null,
  differential_oil: null,
  hydraulic_oil: null,

  // =========================
  // FILTROS
  // =========================
  fuel_filter: null,
  engine_oil_filter: null,
  air_filter: null,
  hydraulic_filter: null,

  // =========================
  // CORREAS
  // =========================
  water_pump_belt: null,
  alternator_belt: null,
  other_belts: [],

  // =========================
  // DIMENSIONES
  // =========================
  machine_weight: 0,
  transport_length: 0,
  transport_width: 0,
  transport_height: 0,

  // =========================
  // CAPACIDAD DE TRABAJO
  // =========================
  work_capacity: 0,
  work_capacity_unit: '',

  // =========================
  // COMPATIBILIDAD
  // =========================
  belt_codes: '',
  filter_codes: '',
  oil_type: '',
  oil_capacity: 0,
  hydraulic_oil_type: '',
  hydraulic_oil_capacity: 0
});

  useEffect(() => {
    fetchMachinery();
  }, []);

  useEffect(() => {
    const filtered = machinery.filter(m =>
      m.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      m.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
      m.brand.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredMachinery(filtered);
  }, [searchTerm, machinery]);

  const fetchMachinery = async () => {
    try {
      const response = await axios.get(`${API}/machinery`);
      setMachinery(response.data);
      setFilteredMachinery(response.data);
    } catch (error) {
      toast.error('Error al cargar maquinaria');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingMachinery) {
        await axios.put(`${API}/machinery/${editingMachinery.id}`, formData);
        toast.success('Maquinaria actualizada');
      } else {
        await axios.post(`${API}/machinery`, formData);
        toast.success('Maquinaria creada');
      }
      setDialogOpen(false);
      resetForm();
      fetchMachinery();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar esta maquinaria?')) return;
    try {
      await axios.delete(`${API}/machinery/${id}`);
      toast.success('Maquinaria eliminada');
      fetchMachinery();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      internal_code: '',
      type: '',
      brand: '',
      model: '',
      serial_number: '',
      chassis_vin: '',
      year: new Date().getFullYear(),

      engine_hours: 0,
      odometer: 0,
      status: 'operational',
      responsible: '',
      location: '',
      project: '',
      notes: '',

      engine_brand: '',
      engine_model: '',
      engine_serial: '',
      engine_configuration: '',
      engine_displacement: 0,
      engine_displacement_unit: 'L',
      engine_hp: 0,
      has_turbo: false,

      fuel_type: 'diesel',
      fuel_tank_capacity: 0,
      fuel_tank_unit: 'L',
      fuel_consumption: 0,
      fuel_consumption_unit: 'L/h',

      engine_oil: null,
      transmission_oil: null,
      differential_oil: null,
      hydraulic_oil: null,

      fuel_filter: null,
      engine_oil_filter: null,
      air_filter: null,
      hydraulic_filter: null,

      water_pump_belt: null,
      alternator_belt: null,
      other_belts: [],

      machine_weight: 0,
      transport_length: 0,
      transport_width: 0,
      transport_height: 0,

      work_capacity: 0,
      work_capacity_unit: '',

      belt_codes: '',
      filter_codes: '',
      oil_type: '',
      oil_capacity: 0,
      hydraulic_oil_type: '',
      hydraulic_oil_capacity: 0
    });

    setEditingMachinery(null);
  };

  const openEditDialog = (m) => {
    setEditingMachinery(m);

    setFormData({
      name: m.name || '',
      internal_code: m.internal_code || '',
      type: m.type || '',
      brand: m.brand || '',
      model: m.model || '',
      serial_number: m.serial_number || '',
      chassis_vin: m.chassis_vin || '',
      year: m.year || new Date().getFullYear(),

      engine_hours: m.engine_hours || 0,
      odometer: m.odometer || 0,
      status: m.status || 'operational',
      responsible: m.responsible || '',
      location: m.location || '',
      project: m.project || '',
      notes: m.notes || '',

      engine_brand: m.engine_brand || '',
      engine_model: m.engine_model || '',
      engine_serial: m.engine_serial || '',
      engine_configuration: m.engine_configuration || '',
      engine_displacement: m.engine_displacement || 0,
      engine_displacement_unit: m.engine_displacement_unit || 'L',
      engine_hp: m.engine_hp || 0,
      has_turbo: m.has_turbo || false,

      fuel_type: m.fuel_type || 'diesel',
      fuel_tank_capacity: m.fuel_tank_capacity || 0,
      fuel_tank_unit: m.fuel_tank_unit || 'L',
      fuel_consumption: m.fuel_consumption || 0,
      fuel_consumption_unit: m.fuel_consumption_unit || 'L/h',

      engine_oil: m.engine_oil || null,
      transmission_oil: m.transmission_oil || null,
      differential_oil: m.differential_oil || null,
      hydraulic_oil: m.hydraulic_oil || null,

      fuel_filter: m.fuel_filter || null,
      engine_oil_filter: m.engine_oil_filter || null,
      air_filter: m.air_filter || null,
      hydraulic_filter: m.hydraulic_filter || null,

      water_pump_belt: m.water_pump_belt || null,
      alternator_belt: m.alternator_belt || null,
      other_belts: m.other_belts || [],

      machine_weight: m.machine_weight || 0,
      transport_length: m.transport_length || 0,
      transport_width: m.transport_width || 0,
      transport_height: m.transport_height || 0,

      work_capacity: m.work_capacity || 0,
      work_capacity_unit: m.work_capacity_unit || '',

      belt_codes: m.belt_codes || '',
      filter_codes: m.filter_codes || '',
      oil_type: m.oil_type || '',
      oil_capacity: m.oil_capacity || 0,
      hydraulic_oil_type: m.hydraulic_oil_type || '',
      hydraulic_oil_capacity: m.hydraulic_oil_capacity || 0
    });

    setDialogOpen(true);
  };

  return (
    <div data-testid="machinery-list-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl font-bold text-slate-800" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            Maquinaria
          </h1>
          <p className="text-slate-600 mt-2">Gestión de equipos y vehículos</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button data-testid="add-machinery-button" className="btn-primary">
              <Plus size={20} className="mr-2" />
              Agregar Maquinaria
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                {editingMachinery ? 'Editar Maquinaria' : 'Nueva Maquinaria'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} data-testid="machinery-form" className="space-y-6">

  {/* =====================================================
      IDENTIFICACIÓN
  ====================================================== */}
  <div>
    <h3 className="text-base font-semibold text-slate-800 mb-4">
      Identificación del equipo
    </h3>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Nombre del equipo *
        </label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          className="input-field"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Código interno
        </label>
        <input
          type="text"
          value={formData.internal_code}
          onChange={(e) => setFormData({ ...formData, internal_code: e.target.value })}
          className="input-field mono"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Tipo *
        </label>
        <select
          value={formData.type}
          onChange={(e) => setFormData({ ...formData, type: e.target.value })}
          className="input-field"
          required
        >
          <option value="">Seleccionar tipo</option>
          <option value="Excavadora">Excavadora</option>
          <option value="Retroexcavadora">Retroexcavadora</option>
          <option value="Cargador frontal">Cargador frontal</option>
          <option value="Motoniveladora">Motoniveladora</option>
          <option value="Tractor">Tractor</option>
          <option value="Bulldozer">Bulldozer</option>
          <option value="Rodillo">Rodillo</option>
          <option value="Volqueta">Volqueta</option>
          <option value="Camión">Camión</option>
          <option value="Grúa">Grúa</option>
          <option value="Generador">Generador</option>
          <option value="Otro">Otro</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Marca *
        </label>
        <input
          type="text"
          value={formData.brand}
          onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
          className="input-field"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Modelo *
        </label>
        <input
          type="text"
          value={formData.model}
          onChange={(e) => setFormData({ ...formData, model: e.target.value })}
          className="input-field"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Año *
        </label>
        <input
          type="number"
          value={formData.year}
          onChange={(e) => setFormData({ ...formData, year: Number(e.target.value) })}
          className="input-field"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Número de serie *
        </label>
        <input
          type="text"
          value={formData.serial_number}
          onChange={(e) => setFormData({ ...formData, serial_number: e.target.value })}
          className="input-field mono"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Chasis / VIN
        </label>
        <input
          type="text"
          value={formData.chassis_vin}
          onChange={(e) => setFormData({ ...formData, chassis_vin: e.target.value })}
          className="input-field mono"
        />
      </div>

    </div>
  </div>

  {/* =====================================================
      MOTOR
  ====================================================== */}
  <div className="border-t border-slate-200 pt-5">
    <h3 className="text-base font-semibold text-slate-800 mb-4">
      Motor
    </h3>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Marca del motor
        </label>
        <input
          type="text"
          value={formData.engine_brand}
          onChange={(e) => setFormData({ ...formData, engine_brand: e.target.value })}
          className="input-field"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Modelo del motor
        </label>
        <input
          type="text"
          value={formData.engine_model}
          onChange={(e) => setFormData({ ...formData, engine_model: e.target.value })}
          className="input-field"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Serie del motor
        </label>
        <input
          type="text"
          value={formData.engine_serial}
          onChange={(e) => setFormData({ ...formData, engine_serial: e.target.value })}
          className="input-field mono"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Configuración del motor
        </label>
        <input
          type="text"
          value={formData.engine_configuration}
          onChange={(e) => setFormData({ ...formData, engine_configuration: e.target.value })}
          className="input-field"
          placeholder="4 cilindros en línea, V6, etc."
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Cilindrada
        </label>

        <div className="grid grid-cols-[minmax(0,1fr)_120px] gap-3 w-full">
          <input
            type="text"
            inputMode="decimal"
            value={formData.engine_displacement ?? ''}
            onChange={(e) =>
              setFormData({
                ...formData,
                engine_displacement: e.target.value
              })
            }
            className="input-field w-full"
            placeholder="Ej. 4.4"
          />

          <select
            value={formData.engine_displacement_unit || 'L'}
            onChange={(e) =>
              setFormData({
                ...formData,
                engine_displacement_unit: e.target.value
              })
            }
            className="input-field w-full"
          >
            <option value="L">L</option>
            <option value="cc">cc</option>
          </select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Potencia
        </label>

        <div className="flex gap-2">
          <input
            type="number"
            min="0"
            step="0.1"
            value={formData.engine_hp}
            onChange={(e) =>
              setFormData({
                ...formData,
                engine_hp: e.target.value === '' ? 0 : Number(e.target.value)
              })
            }
            className="input-field flex-1"
          />

          <div className="flex items-center px-3 bg-slate-100 rounded-md text-sm">
            HP
          </div>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Turbo
        </label>

        <select
          value={formData.has_turbo ? 'yes' : 'no'}
          onChange={(e) =>
            setFormData({
              ...formData,
              has_turbo: e.target.value === 'yes'
            })
          }
          className="input-field"
        >
          <option value="no">No</option>
          <option value="yes">Sí</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Horómetro
        </label>
        <input
          type="number"
          min="0"
          step="0.1"
          value={formData.engine_hours}
          onChange={(e) =>
            setFormData({
              ...formData,
              engine_hours: e.target.value === '' ? 0 : Number(e.target.value)
            })
          }
          className="input-field mono"
        />
      </div>

    </div>
  </div>

  {/* =====================================================
      COMBUSTIBLE
  ====================================================== */}
  <div className="border-t border-slate-200 pt-5">
    <h3 className="text-base font-semibold text-slate-800 mb-4">
      Combustible
    </h3>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Tipo de combustible *
        </label>

        <select
          value={formData.fuel_type}
          onChange={(e) =>
            setFormData({
              ...formData,
              fuel_type: e.target.value
            })
          }
          className="input-field"
          required
        >
          <option value="diesel">Diésel</option>
          <option value="gasoline">Gasolina</option>
          <option value="gas">Gas / GNV</option>
          <option value="lpg">GLP</option>
          <option value="electric">Eléctrico</option>
          <option value="hybrid">Híbrido</option>
          <option value="other">Otro</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Capacidad del tanque
        </label>

        <div className="grid grid-cols-[minmax(0,1fr)_120px] gap-3 w-full">
          <input
            type="text"
            inputMode="decimal"
            value={formData.fuel_tank_capacity ?? ''}
            onChange={(e) =>
              setFormData({
                ...formData,
                fuel_tank_capacity: e.target.value
              })
            }
            className="input-field w-full"
            placeholder="Ej. 300"
          />

          <select
            value={formData.fuel_tank_unit || 'L'}
            onChange={(e) =>
              setFormData({
                ...formData,
                fuel_tank_unit: e.target.value
              })
            }
            className="input-field w-full"
          >
            <option value="L">L</option>
          </select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Consumo promedio
        </label>

        <div className="grid grid-cols-[minmax(0,1fr)_140px] gap-3 w-full">
          <input
            type="text"
            inputMode="decimal"
            value={formData.fuel_consumption ?? ''}
            onChange={(e) =>
              setFormData({
                ...formData,
                fuel_consumption: e.target.value
              })
            }
            className="input-field w-full"
            placeholder="Ej. 18"
          />

          <select
            value={formData.fuel_consumption_unit || 'L/h'}
            onChange={(e) =>
              setFormData({
                ...formData,
                fuel_consumption_unit: e.target.value
              })
            }
            className="input-field w-full"
          >
            <option value="L/h">L/h</option>
            <option value="L/km">L/km</option>
            <option value="km/L">km/L</option>
          </select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Odómetro
        </label>

        <input
          type="number"
          min="0"
          step="0.1"
          value={formData.odometer}
          onChange={(e) =>
            setFormData({
              ...formData,
              odometer: e.target.value === '' ? 0 : Number(e.target.value)
            })
          }
          className="input-field mono"
        />
      </div>

    </div>
  </div>

  {/* =====================================================
      LUBRICANTES
  ====================================================== */}
  <div className="border-t border-slate-200 pt-5">
    <h3 className="text-base font-semibold text-slate-800 mb-4">
      Lubricantes
    </h3>

    <div className="space-y-4">

      {[
        {
          key: 'engine_oil',
          title: 'Aceite de motor'
        },
        {
          key: 'transmission_oil',
          title: 'Aceite de caja / convertidor'
        },
        {
          key: 'differential_oil',
          title: 'Aceite de diferencial / mando'
        },
        {
          key: 'hydraulic_oil',
          title: 'Aceite hidráulico'
        }
      ].map((item) => {
        const data = formData[item.key] || {};

        return (
          <div
            key={item.key}
            className="border border-slate-200 rounded-md p-4"
          >
            <h4 className="font-medium text-slate-800 mb-3">
              {item.title}
            </h4>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">

              <input
                type="number"
                min="0"
                step="0.01"
                placeholder="Cantidad"
                value={data.quantity ?? ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    [item.key]: {
                      ...data,
                      quantity:
                        e.target.value === ''
                          ? null
                          : Number(e.target.value)
                    }
                  })
                }
                className="input-field"
              />

              <input
                type="text"
                placeholder="Tipo"
                value={data.type || ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    [item.key]: {
                      ...data,
                      type: e.target.value
                    }
                  })
                }
                className="input-field"
              />

              <input
                type="text"
                placeholder="Marca"
                value={data.brand || ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    [item.key]: {
                      ...data,
                      brand: e.target.value
                    }
                  })
                }
                className="input-field"
              />

              <input
                type="text"
                placeholder="Especificación"
                value={data.specification || ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    [item.key]: {
                      ...data,
                      specification: e.target.value
                    }
                  })
                }
                className="input-field"
              />

            </div>
          </div>
        );
      })}

    </div>
  </div>

  {/* =====================================================
      FILTROS
  ====================================================== */}
  <div className="border-t border-slate-200 pt-5">
    <h3 className="text-base font-semibold text-slate-800 mb-4">
      Filtros
    </h3>

    <div className="space-y-4">

      {[
        {
          key: 'fuel_filter',
          title: 'Filtro de combustible'
        },
        {
          key: 'engine_oil_filter',
          title: 'Filtro de aceite de motor'
        },
        {
          key: 'air_filter',
          title: 'Filtro de aire'
        },
        {
          key: 'hydraulic_filter',
          title: 'Filtro hidráulico'
        }
      ].map((item) => {
        const data = formData[item.key] || {};

        return (
          <div
            key={item.key}
            className="border border-slate-200 rounded-md p-4"
          >
            <h4 className="font-medium text-slate-800 mb-3">
              {item.title}
            </h4>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">

              <input
                type="text"
                placeholder="Serie / Código"
                value={data.serial || data.code || ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    [item.key]: {
                      ...data,
                      serial: e.target.value
                    }
                  })
                }
                className="input-field mono"
              />

              <input
                type="text"
                placeholder="Marca"
                value={data.brand || ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    [item.key]: {
                      ...data,
                      brand: e.target.value
                    }
                  })
                }
                className="input-field"
              />

              <input
                type="text"
                placeholder="Proveedor"
                value={data.supplier || ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    [item.key]: {
                      ...data,
                      supplier: e.target.value
                    }
                  })
                }
                className="input-field"
              />

            </div>
          </div>
        );
      })}

    </div>
  </div>

  {/* =====================================================
      CORREAS
  ====================================================== */}
  <div className="border-t border-slate-200 pt-5">
    <h3 className="text-base font-semibold text-slate-800 mb-4">
      Correas
    </h3>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

      {[
        {
          key: 'water_pump_belt',
          title: 'Correa bomba de agua'
        },
        {
          key: 'alternator_belt',
          title: 'Correa alternador'
        }
      ].map((item) => {
        const data = formData[item.key] || {};

        return (
          <div
            key={item.key}
            className="border border-slate-200 rounded-md p-4"
          >
            <h4 className="font-medium text-slate-800 mb-3">
              {item.title}
            </h4>

            <div className="space-y-3">

              <input
                type="text"
                placeholder="Serie / Código"
                value={data.serial || data.code || ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    [item.key]: {
                      ...data,
                      serial: e.target.value
                    }
                  })
                }
                className="input-field mono"
              />

              <input
                type="text"
                placeholder="Marca"
                value={data.brand || ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    [item.key]: {
                      ...data,
                      brand: e.target.value
                    }
                  })
                }
                className="input-field"
              />

              <input
                type="text"
                placeholder="Proveedor"
                value={data.supplier || ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    [item.key]: {
                      ...data,
                      supplier: e.target.value
                    }
                  })
                }
                className="input-field"
              />

            </div>
          </div>
        );
      })}

    </div>
  </div>

  {/* =====================================================
      DIMENSIONES
  ====================================================== */}
  <div className="border-t border-slate-200 pt-5">
    <h3 className="text-base font-semibold text-slate-800 mb-4">
      Dimensiones y transporte
    </h3>

    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Peso (kg)
        </label>
        <input
          type="number"
          min="0"
          step="0.01"
          value={formData.machine_weight}
          onChange={(e) =>
            setFormData({
              ...formData,
              machine_weight:
                e.target.value === '' ? 0 : Number(e.target.value)
            })
          }
          className="input-field"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Longitud transporte (m)
        </label>
        <input
          type="number"
          min="0"
          step="0.01"
          value={formData.transport_length}
          onChange={(e) =>
            setFormData({
              ...formData,
              transport_length:
                e.target.value === '' ? 0 : Number(e.target.value)
            })
          }
          className="input-field"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Ancho transporte (m)
        </label>
        <input
          type="number"
          min="0"
          step="0.01"
          value={formData.transport_width}
          onChange={(e) =>
            setFormData({
              ...formData,
              transport_width:
                e.target.value === '' ? 0 : Number(e.target.value)
            })
          }
          className="input-field"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Altura transporte (m)
        </label>
        <input
          type="number"
          min="0"
          step="0.01"
          value={formData.transport_height}
          onChange={(e) =>
            setFormData({
              ...formData,
              transport_height:
                e.target.value === '' ? 0 : Number(e.target.value)
            })
          }
          className="input-field"
        />
      </div>

    </div>
  </div>

  {/* =====================================================
      OPERACIÓN
  ====================================================== */}
  <div className="border-t border-slate-200 pt-5">
    <h3 className="text-base font-semibold text-slate-800 mb-4">
      Operación
    </h3>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Responsable
        </label>
        <input
          type="text"
          value={formData.responsible}
          onChange={(e) =>
            setFormData({
              ...formData,
              responsible: e.target.value
            })
          }
          className="input-field"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Ubicación
        </label>
        <input
          type="text"
          value={formData.location}
          onChange={(e) =>
            setFormData({
              ...formData,
              location: e.target.value
            })
          }
          className="input-field"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Proyecto
        </label>
        <input
          type="text"
          value={formData.project}
          onChange={(e) =>
            setFormData({
              ...formData,
              project: e.target.value
            })
          }
          className="input-field"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Estado
        </label>
        <select
          value={formData.status}
          onChange={(e) =>
            setFormData({
              ...formData,
              status: e.target.value
            })
          }
          className="input-field"
        >
          <option value="operational">Operacional</option>
          <option value="maintenance">En mantenimiento</option>
          <option value="inactive">Inactivo</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Capacidad de trabajo
        </label>

        <input
          type="number"
          min="0"
          step="0.01"
          value={formData.work_capacity}
          onChange={(e) =>
            setFormData({
              ...formData,
              work_capacity:
                e.target.value === '' ? 0 : Number(e.target.value)
            })
          }
          className="input-field"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Unidad de capacidad
        </label>

        <input
          type="text"
          value={formData.work_capacity_unit}
          onChange={(e) =>
            setFormData({
              ...formData,
              work_capacity_unit: e.target.value
            })
          }
          className="input-field"
          placeholder="m³, toneladas, t/h, etc."
        />
      </div>

    </div>
  </div>

  {/* =====================================================
      COMPATIBILIDAD / CÓDIGOS
  ====================================================== */}
  <div className="border-t border-slate-200 pt-5">
    <h3 className="text-base font-semibold text-slate-800 mb-4">
      Códigos y referencias
    </h3>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Códigos de correas
        </label>
        <input
          type="text"
          value={formData.belt_codes}
          onChange={(e) =>
            setFormData({
              ...formData,
              belt_codes: e.target.value
            })
          }
          className="input-field mono"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Códigos de filtros
        </label>
        <input
          type="text"
          value={formData.filter_codes}
          onChange={(e) =>
            setFormData({
              ...formData,
              filter_codes: e.target.value
            })
          }
          className="input-field mono"
        />
      </div>

    </div>
  </div>

  {/* =====================================================
      NOTAS
  ====================================================== */}
  <div className="border-t border-slate-200 pt-5">
    <label className="block text-sm font-medium text-slate-700 mb-2">
      Notas
    </label>

    <textarea
      value={formData.notes}
      onChange={(e) =>
        setFormData({
          ...formData,
          notes: e.target.value
        })
      }
      className="input-field"
      rows={4}
      placeholder="Observaciones generales de la maquinaria..."
    />
  </div>

  {/* BOTONES */}
  <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">

    <Button
      type="button"
      onClick={() => {
        setDialogOpen(false);
        resetForm();
      }}
      className="btn-secondary"
    >
      Cancelar
    </Button>

    <Button
      type="submit"
      data-testid="save-machinery-button"
      className="btn-primary"
    >
      {editingMachinery ? 'Actualizar maquinaria' : 'Crear maquinaria'}
    </Button>

  </div>

</form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
          <input
            type="text"
            data-testid="search-input"
            placeholder="Buscar maquinaria..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input-field pl-10"
          />
        </div>
      </div>

      {/* Machinery Cards */}
      {loading ? (
        <div className="text-center py-12">
          Cargando...
        </div>
      ) : filteredMachinery.length === 0 ? (
        <div
          className="text-center py-16 text-slate-500 bg-white border border-slate-200 rounded-xl"
          data-testid="machinery-empty"
        >
          <div className="text-lg font-medium">
            No se encontró maquinaria
          </div>
          <p className="text-sm mt-1">
            Intente cambiar el criterio de búsqueda.
          </p>
        </div>
      ) : (
        <div
          className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-6"
          data-testid="machinery-cards"
        >
          {filteredMachinery.map((m) => {
            const mainImage =
              Array.isArray(m.images) && m.images.length > 0
                ? (m.images[0].startsWith("/uploads/") ? `${process.env.REACT_APP_BACKEND_URL}${m.images[0]}` : m.images[0])
                : null;

            const statusLabel =
              m.status === 'operational'
                ? 'Operacional'
                : m.status === 'maintenance'
                  ? 'Mantenimiento'
                  : 'Inactivo';

            const fuelLabel = m.fuel_type
              ? m.fuel_type.charAt(0).toUpperCase() + m.fuel_type.slice(1)
              : '—';

            return (
              <div
                key={m.id}
                data-testid={`machinery-card-${m.id}`}
                className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-200"
              >
                <Link to={`/machinery/${m.id}`} className="block">
                  <div className="relative h-52 bg-slate-100 overflow-hidden">
                    {mainImage ? (
                      <img
                        src={mainImage}
                        alt={m.name}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center text-slate-400">
                        <div className="text-5xl mb-2">🚜</div>
                        <span className="text-sm">
                          Sin fotografía
                        </span>
                      </div>
                    )}

                    <div className="absolute top-3 right-3">
                      <span
                        className={`status-badge status-${m.status} shadow-sm`}
                      >
                        {statusLabel}
                      </span>
                    </div>
                  </div>
                </Link>

                <div className="p-5">

                  <div className="mb-4">
                    <h3
                      className="text-xl font-bold text-slate-800 truncate"
                      title={m.name}
                      style={{
                        fontFamily: 'Barlow Condensed, sans-serif'
                      }}
                    >
                      {m.name}
                    </h3>

                    <p className="text-sm text-slate-500 mt-1 truncate">
                      {m.type || 'Sin tipo'}
                      {(m.brand || m.model) && (
                        <>
                          {' · '}
                          {m.brand || ''}
                          {m.brand && m.model ? ' ' : ''}
                          {m.model || ''}
                        </>
                      )}
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-3 mb-5">

                    <div className="bg-slate-50 rounded-lg p-3">
                      <p className="text-xs text-slate-500 uppercase tracking-wide">
                        Horómetro
                      </p>
                      <p className="text-lg font-semibold text-slate-800 mono mt-1">
                        {Number(m.engine_hours || 0).toFixed(1)}
                        <span className="text-xs font-normal ml-1 text-slate-500">
                          h
                        </span>
                      </p>
                    </div>

                    <div className="bg-slate-50 rounded-lg p-3">
                      <p className="text-xs text-slate-500 uppercase tracking-wide">
                        N° Serie
                      </p>
                      <p
                        className="text-sm font-semibold text-slate-800 mono mt-2 truncate"
                        title={m.serial_number || 'Sin serie'}
                      >
                        {m.serial_number || '—'}
                      </p>
                    </div>

                    <div className="bg-slate-50 rounded-lg p-3">
                      <p className="text-xs text-slate-500 uppercase tracking-wide">
                        Combustible
                      </p>
                      <p className="text-sm font-semibold text-slate-800 mt-2">
                        {fuelLabel}
                      </p>
                    </div>

                    <div className="bg-slate-50 rounded-lg p-3">
                      <p className="text-xs text-slate-500 uppercase tracking-wide">
                        Año
                      </p>
                      <p className="text-sm font-semibold text-slate-800 mt-2">
                        {m.year || '—'}
                      </p>
                    </div>

                  </div>

                  <div className="flex items-center gap-2 pt-4 border-t border-slate-200">

                    <Link
                      to={`/machinery/${m.id}`}
                      className="flex-1"
                      data-testid={`view-machinery-${m.id}`}
                    >
                      <Button
                        className="w-full flex items-center justify-center gap-2 bg-slate-800 hover:bg-slate-700 text-white rounded-lg"
                      >
                        <Eye size={17} />
                        Ver
                      </Button>
                    </Link>

                    <button
                      data-testid={`edit-machinery-${m.id}`}
                      onClick={() => openEditDialog(m)}
                      className="p-2.5 rounded-lg border border-slate-200 hover:bg-orange-50 hover:border-orange-200 transition-colors"
                      title="Editar maquinaria"
                    >
                      <Edit size={18} className="text-orange-500" />
                    </button>

                    <button
                      data-testid={`delete-machinery-${m.id}`}
                      onClick={() => handleDelete(m.id)}
                      className="p-2.5 rounded-lg border border-slate-200 hover:bg-red-50 hover:border-red-200 transition-colors"
                      title="Eliminar maquinaria"
                    >
                      <Trash2 size={18} className="text-red-500" />
                    </button>

                  </div>

                </div>
              </div>
            );
          })}
        </div>
      )}

    </div>
  );
};

export default MachineryList;